import { Download as DownloadIcon, FilterList as FilterListIcon, Search as SearchIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import type { MediaTag } from "../../admin/dictionaries/mediaTagsApi";
import type { Image } from "./ImagePreview";
import PrettyImagePreview from "./PrettyImagePreview";

const ITEM_WIDTH = 296;
const DEFAULT_ITEM_HEIGHT = 300;

interface Props {
  isLoading: boolean;
  images: Image[];
  tags: MediaTag[];
  selectedImages: Image[];
  selectionMode: boolean;
  search: { text: string; tags: string[] };
  itemHeight?: number;
  onSearchChange: (text: string, tags: string[]) => void;
  onToggleImageSelection: (image: Image) => void;
  onImageClick: (image: Image) => void;
  onSelectAllImages: () => void;
  onClearSelection: () => void;
  onDownloadImages: (type: "original" | "www" | "thumbnail") => void | Promise<void>;
}

const PrettyImagesPreview: FC<Props> = ({
  isLoading,
  images,
  tags,
  selectedImages,
  selectionMode,
  search,
  itemHeight = DEFAULT_ITEM_HEIGHT,
  onSearchChange,
  onToggleImageSelection,
  onDownloadImages,
  onSelectAllImages,
  onClearSelection,
  onImageClick,
}) => {
  const selectedIds = new Set(selectedImages.map((i) => i.id));
  const toggleTag = (tagId: string) => {
    const newTags = search.tags.includes(tagId) ? search.tags.filter((id) => id !== tagId) : [...search.tags, tagId];
    onSearchChange(search.text, newTags);
  };

  const downloadDisabled = selectedIds.size === 0;

  return (
    <Stack direction="column">
      <Menu
        selectionMode={selectionMode}
        search={search}
        onSearchChange={onSearchChange}
        tags={tags}
        toggleTag={toggleTag}
        selectedIds={selectedIds}
        images={images}
        downloadDisabled={downloadDisabled}
        onDownloadImages={onDownloadImages}
      />
      {isLoading && <LinearProgress />}

      {selectionMode && (
        <Stack direction="row" useFlexGap spacing={1} sx={{ gap: 1, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            Tryb zaznaczania aktywny — kliknij dowolne zdjęcie, aby je dodać lub usunąć z wyboru.
          </Typography>
          <Button size="small" variant="text" onClick={onSelectAllImages}>
            Zaznacz wszystkie
          </Button>
          <Button size="small" variant="text" onClick={onClearSelection}>
            Odznacz wszystkie
          </Button>
        </Stack>
      )}

      {images.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            border: "2px dashed rgba(0,0,0,0.1)",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" color="textSecondary" gutterBottom>
            Brak zdjęć dla podanych kryteriów
          </Typography>
          <Typography variant="body2" color="textDisabled">
            Zmień frazę wyszukiwania lub usuń filtry.
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: "repeat(5, minmax(0, 1fr))",
              xl: "repeat(6, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {images.map((item) => (
            <PrettyImagePreview
              key={item.id}
              image={item}
              width={ITEM_WIDTH}
              height={itemHeight}
              selectMode={selectedImages.length != 0}
              selected={selectedIds.has(item.id)}
              onToggleSelection={() => onToggleImageSelection(item)}
              onImageClick={() => onImageClick(item)}
            />
          ))}
        </Box>
      )}
    </Stack>
  );
};

export default PrettyImagesPreview;

interface MenuProps {
  selectionMode: boolean;
  search: { text: string; tags: string[] };
  onSearchChange: (text: string, tags: string[]) => void;
  tags: MediaTag[];
  toggleTag: (tagId: string) => void;
  selectedIds: Set<string>;
  images: Image[];
  downloadDisabled: boolean;
  onDownloadImages: (type: "original" | "www" | "thumbnail") => void | Promise<void>;
}

const Menu = ({
  selectionMode,
  search,
  onSearchChange,
  tags,
  toggleTag,
  selectedIds,
  images,
  downloadDisabled,
  onDownloadImages,
}: MenuProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 3,
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 2,
        bgcolor: selectionMode ? "rgba(53, 87, 161, 0.03)" : "background.paper",
        transition: "background-color 0.2s ease",
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder="Szukaj: wycieczka, region, tag, tytuł zdjęcia…"
        value={search.text}
        onChange={(e) => onSearchChange(e.target.value, search.tags)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          },
        }}
        sx={{ mb: 2 }}
      />
      <Stack direction="row" spacing={1} useFlexGap sx={{ gap: 1, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
        <FilterListIcon fontSize="small" color="action" />
        <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5, fontWeight: "bold" }}>
          Szybkie filtry:
        </Typography>
        {tags
          .filter((t) => t.isPopular)
          .map((tag) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              clickable
              color={search.tags.includes(tag.id) ? "primary" : "default"}
              variant={search.tags.includes(tag.id) ? "filled" : "outlined"}
              onClick={() => toggleTag(tag.id)}
            />
          ))}
      </Stack>

      <Divider sx={{ mb: 2 }} />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" } }}
      >
        <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
          <Typography variant="body2" color="textSecondary">
            {selectionMode ? (
              <>
                Zaznaczono: <strong>{selectedIds.size}</strong> z {images.length} widocznych
              </>
            ) : (
              <>
                Widocznych zdjęć: <strong>{images.length}</strong>
              </>
            )}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
          <Tooltip title={downloadDisabled ? "Zaznacz co najmniej jedno zdjęcie, aby pobrać pliki." : ""} arrow>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={downloadDisabled}
                onClick={() => onDownloadImages("www")}
              >
                Pobierz zaznaczone (web)
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={downloadDisabled ? "Zaznacz co najmniej jedno zdjęcie, aby pobrać pliki." : ""} arrow>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                disabled={downloadDisabled}
                onClick={() => onDownloadImages("original")}
              >
                Pobierz oryginały
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};
